import { Doc } from '@dadohr/shared/domainTypes/document';
import { AsanaIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/asana';
import styled from '@emotion/styled';
import { Button, Dialog, DialogContent, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { InfoBox } from '../../../../../../components/InfoBox';
import ENVS from '../../../../../../env.json';
import { useTranslation } from '../../../../../../i18next';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { PageOAuthRedirectProps } from '../../../../../OAuth/pages/Redirect';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import { OAuthSetupDialog } from '../oauth';
import { removeAsanaConfig, useAsanaConfig } from './service';

const NAME = 'Asana';

export const ASANA_OAUTH_REDIRECT_PROPS: PageOAuthRedirectProps = {
  integrationName: NAME,
  cloudFn: 'asana_exchangeCodeAndStoreCredentials'
};

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const OAUTH = ENVS.asana.oauth;

const InitialSetupDialog = ({
  orgId,
  open,
  onClose
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <OAuthSetupDialog
      orgId={orgId}
      open={open}
      onClose={onClose}
      name={NAME}
      oauthUrl={'https://app.asana.com/-/oauth_authorize'}
      clientId={OAUTH.clientId}
      redirectUri={OAUTH.redirectUri}
    >
      <Alert severity="info" icon={false}>
        {t('org.integrations.asana.setupInfo.p1')}
      </Alert>
      <Typography component="p" variant="body1">
        {t('org.integrations.asana.setupInfo.p2')}
      </Typography>
      <InfoBox>{t('org.integrations.asana.setupInfo.p3')}</InfoBox>
    </OAuthSetupDialog>
  );
};

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<AsanaIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t('org.integrations.asana.setupInfo.p1')}
        </Alert>

        <IntegrationData>
          <strong>{t('org.integrations.common.email')}</strong>
          <div>{config.data.asanaUser.email}</div>
          <strong>{t('org.integrations.common.accessToken')}</strong>
          <div>{masked(config.data.accessToken)}</div>
          <strong>{t('org.integrations.common.refreshToken')}</strong>
          <div>{masked(config.data.refreshToken)}</div>
        </IntegrationData>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeAsanaConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

interface SetupDialogProps
  extends IntegrationSetupDialogWithConfigProperties<AsanaIntegrationConfig> {
  orgId: string;
}

export const SetupDialog = ({
  config,
  onClose,
  orgId,
  onSuccessfulIntegration, // can be ignored since this integration is not an import source
  open
}: SetupDialogProps) => {
  const setup = !!(config?.data.accessToken && config?.data.refreshToken);
  return (
    <>
      {!setup && (
        <InitialSetupDialog orgId={orgId} open={open} onClose={onClose} />
      )}
      {setup && config && (
        <ExistingSetupDialog config={config} open={open} onClose={onClose} />
      )}
    </>
  );
};

export const AsanaSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useAsanaConfig(org.id, userId);
  const cfg = config || null;

  const active = !!cfg?.data.active;

  return (
    <>
      <IntegrationConfigurationButton
        functionalities={functionalities}
        name={NAME}
        logoUrl="/images/integrations-logos/asana.svg"
        active={active}
        interactive={interactive}
        renderSetupDialog={({
          open,
          onClose,
          onFailure,
          onSuccessfulIntegration
        }: IntegrationSetupDialogProperties) => (
          <SetupDialog
            config={cfg}
            orgId={org.id}
            open={open}
            onClose={onClose}
            onFailure={onFailure}
            onSuccessfulIntegration={onSuccessfulIntegration}
          />
        )}
      />
    </>
  );
};
